import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import {
    ICommonTableAction,
    ICommonTableConfig,
    ICommonTableData,
    ICommonTableService,
} from '../models/common-table.interface';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { RowAction } from '../models/common-table.enum';
import { CommonTableConfig } from '../common-table';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MessageService } from '../../../services/message.service';
import { OrderDirectionEnum } from '../../../enums/orderDirection.Enum';
import { CustomTooltipDirective } from '../../../typing/custom-tooltip.directive';
import { SessionStorageService } from '../../../services/session-storage.service';

@Component({
    selector: 'app-common-table',
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatPaginatorModule,
        MatCheckboxModule,
        MatIconModule,
        MatButtonModule,
        CdkDropList,
        CdkDrag,
        MatMenuModule,
        MatSortModule,
        CustomTooltipDirective,
    ],
    templateUrl: 'common-table.component.html',
    styleUrls: ['./common-table.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CommonTableComponent<T> implements OnInit, AfterViewInit, OnDestroy {
    @Input() service?: ICommonTableService<T>;
    @Input() config: ICommonTableConfig = new CommonTableConfig({});
    @Output() actions = new EventEmitter<ICommonTableAction>();
    rowActions = RowAction;
    dataSource = new MatTableDataSource();
    displayedColumns: string[] = [];
    checkboxColumnName = 'select';
    selection = new SelectionModel<any>(true, []);
    destroy$ = new Subject<void>();
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    protected readonly RowAction = RowAction;
    protected readonly Date = Date;
    protected readonly JSON = JSON;

    constructor(
        private cdr: ChangeDetectorRef,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
    ) {
    }

    ngOnInit(): void {
        this.displayedColumns = this.config.columns.map(col => col.name) || [];
        this.config?.withCheckboxes && this.displayedColumns.unshift(this.checkboxColumnName);
        if (this.config.displayedColumns) {
            this.displayedColumns = this.config.displayedColumns;
        }

        if (this.sessionStorageService.getColumns()) {
            this.displayedColumns = this.sessionStorageService.getColumns() || [];
        }
    }

    ngAfterViewInit(): void {
        if (this.sessionStorageService.getSortItem()) {
            this.config.sortConfig = this.sessionStorageService.getSortItem();
        }

        if (!this.config.filters && this.sessionStorageService.getFiltersValues()) {
            this.config.filters = this.sessionStorageService.getFiltersValues();
        }
        this.initDataSource();
        this.service?.updateData(this.config);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // this.service?.tableData$.complete();
    }

    // For checkbox column
    dataIsBoolean(data: unknown): data is boolean {
        return typeof data === 'boolean';
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }

        this.selection.select(...this.dataSource.data);
    }

    processAction(action: RowAction, data: ICommonTableData[]): void {
        this.actions.emit({ action, data });
    }

    onChangePage(): void {
        this.config.paginatorConfig.itemsPerPage = this.paginator.pageSize;
        this.config.paginatorConfig.pageIndex = this.paginator.pageIndex + 1;
        this.service?.updateData(this.config);
    }

    onSortChange(sort: Sort): void {
        const { active, direction } = sort;

        if (!direction) {
            return;
        }

        const orderDirection = direction.toUpperCase() as OrderDirectionEnum;

        if (!this.config.paginatorConfig.sortBy) {
            this.config.paginatorConfig.sortBy = [`${active}:${orderDirection}`];
        } else {
            this.config.paginatorConfig.sortBy.push(`${active}:${orderDirection}`);
        }

        this.service?.updateData(this.config);
    }

    toggleCol(colName: string): void {
        if (this.displayedColumns.includes(colName)) {
            this.displayedColumns = this.displayedColumns.filter(e => e !== colName);
        } else {
            //todo make it better add on the same position as before
            this.displayedColumns.push(colName);
        }
        this.sessionStorageService.setColumns(this.displayedColumns);
    }

    drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
        this.sessionStorageService.setColumns(this.displayedColumns);
    }

    bulkEdit(rowAction: RowAction): void {
        this.processAction(rowAction, this.selection.selected);
    }

    copyToClipboard(event: Event, value: string): void {
        event.stopPropagation();
        event.preventDefault();
        // Получаем элемент иконки, который находится внутри нажатой кнопки
        const target = event.target as HTMLElement;
        const icon = target.closest('button')?.querySelector('#copy-icon') as HTMLElement;

        if (icon) {
            icon.innerText = 'check'; // Меняем иконку на "check"
            icon.classList.add('tx-green-light'); // Меняем цвет иконки на зеленый
        }

        // Копируем текст в буфер обмена
        navigator.clipboard.writeText(value);

        // Возвращаем иконку обратно через 1 секунду
        setTimeout(() => {
            if (icon) {
                icon.innerText = 'content_copy'; // Возвращаем иконку обратно
                icon.classList.remove('tx-green-light'); // Убираем цвет иконки
            }
        }, 1000);
    }

    autocompleteFilter(value: string, autocompleteFilterName: string): void {
        this.actions.emit({
            action: RowAction.AUTOCOMPLETE_FILTER,
            data: [
                {
                    filterName: autocompleteFilterName,
                    value: value,
                },
            ],
        });
    }

    findLink(value: string | null | undefined): string | undefined {
        const urlRegex = /https?:\/\/[^\s]+/;
        if (typeof value === 'string') {
            const match = value.match(urlRegex);

            if (match) {
                return match[0];
            }
        }

        return undefined;
    }

    playAudio(audioUrl: string): void {
        const audio = new Audio(audioUrl);
        audio.play();
    }

    private initDataSource(): void {
        this.service?.tableData$.pipe(takeUntil(this.destroy$)).subscribe({
            next: tableData => {
                this.dataSource.data = tableData.data;
                this.config.paginatorConfig.totalItems = tableData.meta?.totalItems || 0;
                this.paginator.length = this.config.paginatorConfig.totalItems;
                this.paginator.pageIndex = (this.config.paginatorConfig.pageIndex ?? 1) - 1;
                this.cdr.detectChanges();
            },
            error: () => this.messageService.errorMessage('Ошибка загрузки таблицы'),
        });
    }
}
